import React, { useEffect, useState } from "react";
import "./HomeScreen.css";
import { motion, AnimatePresence } from "framer-motion";
import settingsIcon from "../images/settings.svg";
import panicButtonImg from "../images/panic-button-bg.png";
import droneAlertImg from "../images/drone-alert-bg.png";
import homeIcon from "../images/home-active.png";
import profileIcon from "../images/account_circle.png";
import alertIcon from "../images/alert.png";
import profilePic from "../images/profile-edit.png";
import { Link, useLocation } from "react-router-dom";

const HomeScreen = () => {
  const [isPanicActive, setIsPanicActive] = useState(false);
  const [isDroneActive, setIsDroneActive] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [user, setUser] = useState(null);
  const [droneAlertId, setDroneAlertId] = useState(null);
  const location = useLocation();

  useEffect(() => {
    try {
      const stored = localStorage.getItem("userData");
      if (stored) setUser(JSON.parse(stored));
      else alert("Please log in again.");
    } catch (err) {
      console.error("Invalid JSON in localStorage:", err);
      localStorage.removeItem("userData");
      alert("Session error. Please log in again.");
    }
  }, []);

  // --- Panic Alert ---
  const handlePanic = () => {
    if (!navigator.geolocation) return alert("Location access not supported.");
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const response = await fetch(
          "https://senu-app.azurewebsites.net/api/alerts/panic",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: user.id,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              userName: user.name,
              Message: `🚨 Panic alert triggered by ${user.name} at ${new Date().toLocaleString()}`,
            }),
          }
        );

        if (response.ok) {
          alert("🚨 Panic alert sent to station!");
          setIsPanicActive(true);
        } else {
          const err = await response.json();
          alert("Error: " + (err.message || "Failed to trigger panic alert."));
        }
      } catch (error) {
        alert("Something went wrong: " + error.message);
      }
    });
  };

  // const handleCancelPanic = async () => {
  //   if (!pinInput) return alert("Please enter your 4-digit PIN.");
  //   try {
  //     const response = await fetch(
  //       "https://senu-app.azurewebsites.net/api/alerts/cancel",
  //       {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           userId: user.id,
  //           pin: pinInput,
  //           type: "panic"
  //         }),
  //       }
  //     );

  //     if (response.ok) {
  //       alert("✅ Panic alert cancelled successfully.");
  //       setIsPanicActive(false);
  //       setPinInput("");
  //     } else {
  //       const errorData = await response.json();
  //       alert("❌ Error cancelling panic alert: " + (errorData.message || "Unknown error"));
  //     }
  //   } catch (error) {
  //     alert("❌ Network or server error: " + error.message);
  //   }
  // };

  const handleCancelPanic = async () => {
  if (!pinInput) return alert("Please enter your 4-digit PIN.");

  if (!navigator.geolocation) return alert("Location not supported.");

  navigator.geolocation.getCurrentPosition(async (position) => {
    try {
      const response = await fetch(
        "https://senu-app.azurewebsites.net/api/alerts/cancel",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
            pin: pinInput,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }),
        }
      );

      if (response.ok) {
        alert("✅ Alert cancelled successfully.");
        setIsPanicActive(false);
        setPinInput("");
      } else {
        let errMessage = `Error ${response.status}`;
        try {
          const errorData = await response.json();
          errMessage = errorData.message || errMessage;
        } catch {
          const text = await response.text();
          if (text) errMessage = text;
        }
        alert("❌ Error cancelling alert: " + errMessage);
      }
    } catch (error) {
      alert("❌ Network or server error: " + error.message);
    }
  });
};


  // --- Drone Alert ---
  const sendDroneAlert = async () => {
    if (!navigator.geolocation) return alert("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const requestData = {
          userId: user.id,
          Message: `🚁 Drone alert triggered by ${user.name} at ${new Date().toLocaleString()}`,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };

        const response = await fetch(
          "https://senu-app.azurewebsites.net/api/alerts/drone",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestData)
          }
        );

        if (!response.ok) throw new Error("Failed to send drone alert");
        const data = await response.json();
        setDroneAlertId(data.id); // store ID for cancellation
        setIsDroneActive(true);
        alert("🚁 Drone Alert sent successfully!");
      } catch (error) {
        console.error("Error sending drone alert:", error);
        alert("Error sending drone alert.");
      }
    });
  };

  const handleCancelDrone = async () => {
    if (!pinInput) return alert("Please enter your 4-digit PIN.");
    try {
      const response = await fetch(
        "https://senu-app.azurewebsites.net/api/alerts/cancel",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
            pin: pinInput,
            alertId: droneAlertId,
            type: "drone"
          }),
        }
      );

      if (response.ok) {
        alert("✅ Drone alert cancelled successfully.");
        setIsDroneActive(false);
        setPinInput("");
        setDroneAlertId(null);
      } else {
        const errorData = await response.json();
        alert("❌ Error cancelling drone alert: " + (errorData.message || "Unknown error"));
      }
    } catch (error) {
      alert("❌ Network or server error: " + error.message);
    }
  };

  if (!user) return null;

  return (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={location.pathname}
        className="home-container"
        initial={{ y:"100%", opacity:0.5, scale:0.95 }}
        animate={{ y:0, opacity:1, scale:1 }}
        exit={{ y:"-100%", opacity:0, scale:0.95 }}
        transition={{ type:"spring", stiffness:300, damping:30, duration:0.3 }}
      >
        {/* Top Bar */}
        <div className="top-bar">
          <div className="welcome-profile">
            <img src={profilePic} alt="Profile" className="profile-pic" />
            <span className="welcome-text">Welcome, {user?.name?.split(" ")[0] || "User"}</span>
          </div>
          <img src={settingsIcon} alt="Settings" className="settings-icon" />
        </div>

        {/* Center Buttons */}
        <div className="center-buttons">
          {/* Panic */}
          {!isPanicActive ? (
            <button className="panic-button" onClick={handlePanic}>
              <img src={panicButtonImg} alt="Panic Button" />
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <p style={{ marginBottom:"10px", color:"red", fontWeight:"bold" }}>Panic Activated!</p>
              <input
                type="password"
                placeholder="Enter 4-digit PIN"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                style={{ padding:"8px", borderRadius:"8px", border:"1px solid #ccc", marginBottom:"10px", textAlign:"center", width:"160px" }}
                maxLength={4}
              />
              <button onClick={handleCancelPanic} style={{ backgroundColor:"#222", color:"#fff", padding:"10px 20px", borderRadius:"8px", border:"none" }}>Cancel Panic</button>
            </div>
          )}

          {/* Drone */}
          {!isDroneActive ? (
            <button onClick={sendDroneAlert} className="drone-alert-button">
              <img src={droneAlertImg} alt="Drone Alert" />
            </button>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginTop:"10px" }}>
              <p style={{ marginBottom:"10px", color:"orange", fontWeight:"bold" }}>Drone Alert Active!</p>
              <input
                type="password"
                placeholder="Enter 4-digit PIN"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                style={{ padding:"8px", borderRadius:"8px", border:"1px solid #ccc", marginBottom:"10px", textAlign:"center", width:"160px" }}
                maxLength={4}
              />
              <button onClick={handleCancelDrone} style={{ backgroundColor:"#222", color:"#fff", padding:"10px 20px", borderRadius:"8px", border:"none" }}>Cancel Drone Alert</button>
            </div>
          )}
        </div>

        {/* Bottom Nav Bar */}
        <nav className="bottom-nav">
          <Link to="/user-alert-history">
            <motion.img src={alertIcon} alt="Alert" className="nav-icon"
              animate={{ scale: location.pathname === "/user-alert-history" ? 1.8 : 1, y: location.pathname === "/user-alert-history" ? -5 : 0 }}
              transition={{ type:"spring", stiffness:400, damping:25 }}
            />
          </Link>

          <motion.img src={homeIcon} alt="Home" className="nav-icon center-icon" layout
            animate={{ scale: location.pathname === "/home" ? 1 : 1 }} transition={{ type:"spring", stiffness:200, damping:20 }} style={{ marginBottom:"45px" }}
          />

          <Link to="/edit-profile">
            <motion.img src={profileIcon} alt="Profile" className="nav-icon"
              animate={{ scale: location.pathname === "/profile" ? 1.5 : 1, y: location.pathname === "/profile" ? -5 : 0 }}
              transition={{ type:"spring", stiffness:400, damping:25 }}
            />
          </Link>
        </nav>
      </motion.div>
    </AnimatePresence>
  );
};

export default HomeScreen;
